import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SearchCountryField, CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input-gg';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { OrganizationService } from '../../services/organization.service';
import { OtpAction, Utilities } from '../../shared/utils';
import { finalize } from 'rxjs';
import { LoaderService } from '../../services/loader.service';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.css',
})
export class SigninComponent {
  loginViaEmail: boolean = true;
  email: string = '';
  emailError: any = '';
  submitted = false;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  phoneForm = new FormGroup({
    phone: new FormControl(undefined, [Validators.required]),
  });
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];
  separateDialCode = false;
  initialTimerMins: number = 2;
  minutes: number = this.initialTimerMins;
  seconds: number = 0;
  timerInterval: any;
  orgArray: any = [];
  filteredOrgArray: any = [];
  organizationSearchTerm: string = '';
  OTP: string = '';
  allowNumber: boolean = true;
  successResponse: any;
  loader: boolean = false;
  invalidOTP: boolean = false;
  rememberMe: boolean = false;
  OTPsuccess: boolean = false;
  numberPlaceholder = '07700 168247';

  constructor(
    private authService: AuthService,
    private router: Router,
    private organizationService: OrganizationService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private loaderService: LoaderService,
    private toast: ToastService
  ) { }

  startTimer() {
    this.timerInterval = setInterval(() => {
      if (this.seconds > 0) {
        this.seconds--;
      } else if (this.minutes > 0) {
        this.minutes--;
        this.seconds = 59;
      } else {
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  resendCode() {
    this.invalidOTP = false;
    if (this.loginViaEmail) {
      this.submitEmail(this.email);
    } else {
      const phoneNumber: any = this.phoneForm.get('phone')?.value;
      this.submitPhone(phoneNumber.e164Number);
    }
  }

  resetAndStartTimer() {
    this.minutes = this.initialTimerMins;
    this.seconds = 0;
    clearInterval(this.timerInterval);
    this.startTimer();
  }

  toggleLoginMethod() {
    this.loginViaEmail = !this.loginViaEmail;
    this.emailError = null;
    this.submitted = false;
    this.email = '';
    this.phoneForm.reset();
  }

  goBack() {
    this.submitted = false;
    this.emailError = null;
    this.email = '';
    this.phoneForm.reset();
    this.successResponse = null;
    this.OTP = '';
    this.OTPsuccess = false;
    this.invalidOTP = false;
    this.loginViaEmail = true;
  }

  onOtpChange(event: any) {
    if (event.length === 6) {
      this.OTP = event;
    }
  }

  emailChanged() {
    this.emailError = '';
  }

  submitEmail(data: string) {
    this.startLoader();
    this.authService.authenticateOrVerify(data, OtpAction.LOGIN)
      .pipe(finalize(() => {
        this.stopLoader();
      }))
      .subscribe({
        next: (response) => {
          this.successResponse = response;
          this.submitted = true;
          this.resetAndStartTimer();
        },
        error: (error) => {
          const msg = Utilities.getErrorMessage(error);
          this.emailError = msg;
          this.email = '';
        },
      });
  }

  submitPhone(number: string) {
    this.startLoader();
    this.authService.authenticateOrVerify(number, OtpAction.LOGIN)
      .pipe(finalize(() => {
        this.stopLoader();
      }))
      .subscribe({
        next: (response) => {
          this.successResponse = response;
          this.submitted = true;
          this.resetAndStartTimer();
        },
        error: (error) => {
          const msg = Utilities.getErrorMessage(error);
          this.emailError = msg;
          this.email = '';
        },
      });
  }

  checkOTP() {
    let validateObjet;
    this.invalidOTP = false;
    this.startLoader();
    if (this.OTP) {
      if (this.loginViaEmail) {
        validateObjet = {
          Email: this.email,
          OTP: this.OTP,
        };
      } else {
        const phoneNumber: any = this.phoneForm.get('phone')?.value;
        validateObjet = {
          Mobile: phoneNumber.e164Number,
          OTP: this.OTP,
        };
      }
    } else {
      this.invalidOTP = true;
      this.stopLoader();
    }

    this.authService.verifyOTP(validateObjet)
      .pipe(finalize(() => {
        this.stopLoader();
      }))
      .subscribe({
        next: (response) => {
          this.successResponse = response;
          this.selectOrg(response);

          this.invalidOTP = false;
          this.OTPsuccess = true;
        },
        error: (error) => {
          this.invalidOTP = true;
          const msg = Utilities.getErrorMessage(error);
          this.toast.error(msg);
        },
      });
  }

  selectOrg(response: any) {
    this.orgArray = [];
    this.filteredOrgArray = [];
    this.organizationSearchTerm = '';
    if (response.multi) {
      this.organizationService.setOrgToLocalStorage(true, this.successResponse.organizations);
      this.orgArray = response.organizations.map((element: any) => {
        return {
          orgCode: element?.orgAddress[0]?.orgCode,
          orgName: element?.orgAddress[0]?.orgName,
          address: Utilities.orgAddressFilter(element?.orgAddress[0]),
          email: element?.user?.email,
          mobile: element?.user?.mobile,
          response: element
        };
      }).sort((a: any, b: any) => a.orgName.localeCompare(b.orgName));
      this.filteredOrgArray = [...this.orgArray];
    }
    else {
      this.organizationService.setOrgToLocalStorage(false);
      this.orgArray = [];

      if (isPlatformBrowser(this.platformId)) {
        this.authService.setAuthData(response.organizations[0]);
      }
      this.authService.callForUpdateLastLogin(response.organizations[0].user.email, response.organizations[0].orgAddress[0].orgCode);
      this.afterLoggedIn();
    }
  }

  filterOrganizations() {
    const searchTerm = this.organizationSearchTerm.trim().toLowerCase();

    if (!searchTerm) {
      this.filteredOrgArray = [...this.orgArray];
      return;
    }

    this.filteredOrgArray = this.orgArray.filter((org: any) =>
      [org.orgName, org.orgCode, org.address].some((value) =>
        String(value ?? '').toLowerCase().includes(searchTerm)
      )
    );
  }

  clearOrganizationSearch() {
    this.organizationSearchTerm = '';
    this.filteredOrgArray = [...this.orgArray];
  }

  loginUsingOrg(org: any) {
    this.startLoader();
    if (isPlatformBrowser(this.platformId)) {
      this.authService.setAuthData(org.response);
    }
    this.authService.callForUpdateLastLogin(org.email, org.orgCode);
    this.afterLoggedIn();
    setTimeout(() => {
      this.stopLoader();
    }, Utilities.EMPTY_LOADER_TIMER);
  }

  signUp() {
    this.router.navigate(['/quote-request/register']);
  }

  onSubmit(otpLogin: boolean = false) {
    this.emailError = null;

    this.submitted = false;
    if (otpLogin) {
      this.submitted = true;
      this.checkOTP();
      return;
    }

    if (this.loginViaEmail) {
      this.emailError = null;
      if (!this.email) {
        this.emailError = 'Email is required.';
        return;
      }
      if (!Utilities.EMAIL_REGEX.test(this.email)) {
        this.emailError = 'Invalid email format.';
        return;
      }
      1;
      this.successResponse = null;
      if (!this.successResponse) {
        this.submitEmail(this.email);
      } else {
        this.checkOTP();
      }
    } else {
      this.phoneForm.markAllAsTouched();
      if (this.phoneForm.invalid) {
        this.emailError = 'Invalid phone number.';
        return;
      }
      const phoneNumber: any = this.phoneForm.get('phone')?.value;
      if (!phoneNumber) {
        this.emailError = 'Please enter a phone number.';
        return;
      }
      this.successResponse = null;
      this.email = phoneNumber.e164Number;
      this.submitPhone(phoneNumber.e164Number);
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    if (target && target.type === 'tel') {
      const isDigit = /^[0-9]$/.test(event.key);
      if (!isDigit) {
        event.preventDefault();
      }
    }
  }

  startLoader() {
    this.loader = true;
    this.loaderService.start();
  }

  afterLoggedIn() {
    const requote = sessionStorage.getItem('requoteRedirectUrl') || localStorage.getItem('requoteRedirectUrl');
    if (requote) {
      sessionStorage.removeItem('requoteRedirectUrl');
      localStorage.removeItem('requoteRedirectUrl');
      this.router.navigateByUrl(requote);
      return;
    }
    const referralUrl = sessionStorage.getItem('referralRedirectUrl') || localStorage.getItem('referralRedirectUrl');
    if (referralUrl) {
      sessionStorage.removeItem('referralRedirectUrl');
      localStorage.removeItem('referralRedirectUrl');
      this.router.navigateByUrl(referralUrl);
      return;
    }
    this.router.navigate(['dashboard', 'live-vehicles-availability']);
  }

  stopLoader() {
    this.loader = false;
    this.loaderService.stop();
  }

}
